import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  Grid2,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { IconDeviceIpadPlus, IconPencil, IconX } from "@tabler/icons-react";
import { Input } from "./Input";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import {
  CategoryList,
  payableUpadate,
  payableUpadateRecurrence,
} from "../services/payable";
import { useUserStore } from "../stores/user";
import { formatPrice, maskMoney, unMaskMoney } from "../helpers/format";
import Loading from "./Loading";
import CategoryRegister from "./CategoryRegister";
import CategoryEdit from "./CategoryEdit";

const PayableEdit = ({
  open,
  onClose,
  refetch,
  payable,
  pendings,
  recurrence,
  paids,
  isRecurring,
}: any) => {
  const [step, setStep] = useState<"1" | "2" | "3">("1");
  const [maskedValue, setMaskedValue] = useState("");
  const [openCategory, setOpenCategory] = useState(false);
  const [openEditCategory, setEditCategory] = useState(false);

  const mode = pendings
    ? "pendings"
    : recurrence
    ? "recurrence"
    : paids
    ? "paids"
    : "default";
  const allowedSteps: Array<"1" | "2" | "3"> =
    mode === "pendings"
      ? ["1", "2"]
      : mode === "recurrence"
      ? ["1", "2"]
      : mode === "paids"
      ? ["1", "2"]
      : ["1", "2"];

  useEffect(() => {
    if (open) setStep(allowedSteps[0]);
  }, [open, mode]);

  const goNextOrSubmit = () => {
    const idx = allowedSteps.indexOf(step);
    const isLast = idx === allowedSteps.length - 1;

    if (!isLast) {
      if (step === "2") {
        if (!values.amount) {
          toast.info("Preencha todos os campos financeiros");
          return;
        }
      }
      setStep(allowedSteps[idx + 1]);
      return;
    }

    handleSubmit();
  };

  const { user } = useUserStore();
  const storeId = user?.storeId;

  const {
    data,
    isLoading,
    isFetching,
    refetch: refetchCategory,
  } = useQuery({
    queryKey: ["category"],
    queryFn: async () => await CategoryList(),
  });

  const { mutate, isPending } = useMutation({
    mutationFn: payableUpadate,
    onSuccess: () => {
      onClose();
      refetch();
      toast.success("Conta editada com sucesso!");
    },
    onError: (e: any) => {
      const message =
        e?.response?.data?.message ?? e?.message ?? "Erro ao editar a conta!";
      toast.error(message);
    },
  });

  const { mutate: mutateRecurrence, isPending: isPendingRecurrence } =
    useMutation({
      mutationFn: payableUpadateRecurrence,
      onSuccess: () => {
        onClose();
        refetch();
        toast.success("Conta editada com sucesso!");
      },
      onError: (e: any) => {
        const message =
          e?.response?.data?.message ?? e?.message ?? "Erro ao editar a conta!";
        toast.error(message);
      },
    });

  const formik = useFormik({
    initialValues: {
      id: undefined,
      title: "",
      description: "",
      amount: "",
      dueDate: "",
      nextPayble: "",
      paymentDate: "",
      categoryId: "",
      isRecurring: false,
      recurrence: "MONTHLY",
      status: "PENDING",
    },
    onSubmit: (values) => {
      if (!payable) return;

      const normalizeDate = (v: any) => (v ? String(v).split("T")[0] : "");

      const getOriginal = (key: string) => {
        if (key === "amount") return payable.amount;
        if (key === "dueDate") return normalizeDate(payable.dueDate);
        if (key === "paymentDate") return normalizeDate(payable.paymentDate);
        return payable[key];
      };

      const toUTCDate = (s?: string) => {
        if (!s) return undefined;
        const [y, m, d] = s.split("-").map(Number);
        return new Date(Date.UTC(y, m - 1, d));
      };

      const updatedFields = Object.entries(values).reduce(
        (acc, [key, value]) => {
          const current =
            key === "amount" ? Number(unMaskMoney(maskedValue)) : value;

          const currNorm =
            key === "dueDate" || key === "paymentDate" || key === "nextPayble"
              ? normalizeDate(current)
              : current;

          const origNorm = getOriginal(key);

          if (key === "paymentDate" && !currNorm) return acc;

          if (currNorm !== origNorm) {
            acc[key] = current;
          }
          return acc;
        },
        {} as Record<string, any>
      );

      if (values.status === "PAID" && updatedFields.paymentDate == null) {
        updatedFields.paymentDate = new Date();
      }

      const changeRecurrence = values.isRecurring && !payable.isRecurring;
      if (changeRecurrence && values.status !== "PAID") {
        updatedFields.status = "SCHEDULED";
      }
      if (values.isRecurring) {
        updatedFields.recurrence = values.recurrence || "ONETIME";
      }

      if (typeof updatedFields.dueDate === "string") {
        updatedFields.dueDate = toUTCDate(updatedFields.dueDate);
      }
      if (typeof updatedFields.nextPayble === "string") {
        updatedFields.nextPayble = toUTCDate(updatedFields.nextPayble);
      }
      if (typeof updatedFields.paymentDate === "string") {
        updatedFields.paymentDate = toUTCDate(updatedFields.paymentDate);
      }

      if (Object.keys(updatedFields).length === 0) {
        toast.info("Nenhuma alteração feita");
        return;
      }

      const shouldUseRecurrenceApi = !!isRecurring;

      const basePayload: any = {
        id: values.id,
        storeId,
        ...updatedFields,
      };

      if (shouldUseRecurrenceApi) {
        delete basePayload.paymentDate;
        mutateRecurrence(basePayload);
      } else {
        mutate({
          ...basePayload,
          isRecurring: values.isRecurring,
        });
      }
    },
  });

  const { values, handleSubmit, handleChange, setFieldValue, resetForm } =
    formik;

  useEffect(() => {
    if (payable) {
      resetForm({
        values: {
          ...payable,
          amount: Number(payable.amount) || 0,
          dueDate: payable.dueDate?.split("T")[0] || "",
          nextPayble: payable.nextPayble?.split("T")[0] || "",
          paymentDate: payable.paymentDate?.split("T")[0] || "",
        },
      });
      setMaskedValue(formatPrice(Number(payable.amount) || 0));
    }
  }, [payable]);

  return (
    <Dialog maxWidth="xs" open={open} onClose={onClose}>
      <Loading loading={isPending || isPendingRecurrence || isLoading} />
      <DialogContent sx={{ backgroundColor: "secondary.main" }}>
        <Grid2 container spacing={2} padding={1}>
          <Grid2 size={{ xs: 10 }}>
            <Typography fontSize={24} fontWeight={600}>
              Editar Conta
            </Typography>
            <Typography variant="subtitle1" color="secondary.contrastText">
              Alterar os dados de pagamento
            </Typography>
          </Grid2>
          <Grid2 size={{ xs: 2 }}>
            <IconButton onClick={onClose}>
              <IconX />
            </IconButton>
          </Grid2>

          <Grid2 size={{ xs: 12 }}>
            <Tabs
              value={step}
              onChange={(_, v) => setStep(v)}
              textColor="inherit"
              variant="scrollable"
              scrollButtons="auto"
            >
              {allowedSteps.includes("1") && <Tab label="Geral" value="1" />}
              {allowedSteps.includes("2") && (
                <Tab label="Financeiro" value="2" />
              )}
            </Tabs>
          </Grid2>

          {step === "1" && allowedSteps.includes("1") && (
            <>
              <Grid2 size={{ xs: 12 }}>
                <Input
                  id="title"
                  name="title"
                  label="Título"
                  value={values.title}
                  onChange={handleChange}
                />
              </Grid2>
              <Grid2 size={{ xs: 12 }}>
                <Input
                  id="description"
                  name="description"
                  label="Descrição"
                  multiline
                  rows={3}
                  value={values.description}
                  onChange={handleChange}
                />
              </Grid2>
            </>
          )}

          {step === "2" && allowedSteps.includes("2") && (
            <>
              <Grid2 size={{ xs: 12 }}>
                <Input
                  id="amount"
                  name="amount"
                  label="Valor"
                  value={maskedValue}
                  onChange={(e) => {
                    const masked = maskMoney(e.target.value);
                    setMaskedValue(masked);
                    setFieldValue("amount", unMaskMoney(masked));
                  }}
                />
              </Grid2>

              <Grid2 size={{ xs: 12 }}>
                <Stack>
                  <Stack
                    flexDirection={"row"}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                  >
                    <Typography fontWeight={600} fontSize={14}>
                      Categoria
                    </Typography>

                    <Stack direction={"row"} gap={1}>
                      <Tooltip title="Cadastrar categoria">
                        <IconButton onClick={() => setOpenCategory(true)}>
                          <IconDeviceIpadPlus size={14} />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Editar categorias">
                        <IconButton onClick={() => setEditCategory(true)}>
                          <IconPencil size={14} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                  <Select
                    id="categoryId"
                    name="categoryId"
                    value={values.categoryId}
                    onChange={handleChange}
                    fullWidth
                    displayEmpty
                    sx={{ marginTop: 1 }}
                  >
                    <MenuItem value="" disabled>
                      {isLoading || isFetching
                        ? "Carregando categorias..."
                        : "Selecione uma categoria"}
                    </MenuItem>

                    {Array.isArray(data) &&
                      data
                        .filter((c: any) => c?.status === true)
                        .map((c: any) => (
                          <MenuItem key={c.id} value={c.id}>
                            {c.name}
                          </MenuItem>
                        ))}

                    {!isLoading &&
                      !isFetching &&
                      Array.isArray(data) &&
                      data.filter((c: any) => c?.status === true).length ===
                        0 && (
                        <MenuItem value="" disabled>
                          Nenhuma categoria ativa
                        </MenuItem>
                      )}
                  </Select>
                </Stack>
              </Grid2>

              {isRecurring && (
                <Grid2 size={{ xs: 12 }}>
                  <Input
                    id="dueDate"
                    name="dueDate"
                    label="Data de vencimento"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={values.dueDate}
                    onChange={handleChange}
                  />
                </Grid2>
              )}

              {!isRecurring && (
                <Grid2 size={{ xs: 12 }}>
                  <Input
                    id="nextPayble"
                    name="nextPayble"
                    label="Data renovação"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={values.nextPayble}
                    onChange={handleChange}
                  />
                </Grid2>
              )}

              {paids && (
                <Grid2 size={{ xs: 12 }}>
                  <Input
                    id="paymentDate"
                    name="paymentDate"
                    label="Data de pagamento"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={values.paymentDate}
                    onChange={handleChange}
                  />
                </Grid2>
              )}

              {!recurrence && !paids && (
                <Grid2 size={{ xs: 12 }}>
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Checkbox
                      id="paidCheckbox"
                      checked={values.status === "PAID"}
                      onChange={(e) =>
                        setFieldValue(
                          "status",
                          e.target.checked ? "PAID" : "PENDING"
                        )
                      }
                    />
                    <Typography fontSize={14}>Já foi pago?</Typography>
                  </Stack>
                </Grid2>
              )}
            </>
          )}

          <Grid2 size={{ xs: 12 }}>
            <Button fullWidth variant="contained" onClick={goNextOrSubmit}>
              {allowedSteps.indexOf(step) === allowedSteps.length - 1
                ? "Salvar Alterações"
                : "Próximo"}
            </Button>
          </Grid2>
        </Grid2>
      </DialogContent>

      <CategoryRegister
        open={openCategory}
        onClose={() => setOpenCategory(false)}
        refetch={refetchCategory}
      />

      <CategoryEdit
        open={openEditCategory}
        onClose={() => setEditCategory(false)}
        refetch={refetchCategory}
      />
    </Dialog>
  );
};

export default PayableEdit;
